import { useState, useEffect } from 'react';
import { App, UserFavorite, AppWithFavorite } from '@/types';
import { dashboardApi } from '@/utils/api';

export function useApps() {
  const [apps, setApps] = useState<AppWithFavorite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadApps();
  }, []);

  const loadApps = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Loading apps and favorites...');

      const [appsData, favoritesData] = await Promise.all([
        dashboardApi.getApps(),
        dashboardApi.getFavorites(),
      ]);

      console.log('📦 Apps data received:', appsData);
      console.log('⭐ Favorites data received:', favoritesData);

      const favoriteAppIds = new Set(favoritesData.map((f: UserFavorite) => f.appId));

      const appsWithFavorites: AppWithFavorite[] = appsData.map((app: App) => ({
        ...app,
        isFavorite: favoriteAppIds.has(app.id),
      }));

      console.log('✅ Apps with favorites:', appsWithFavorites);
      setApps(appsWithFavorites);
    } catch (err) {
      console.error('❌ Failed to load apps:', err);
      setError('Failed to load apps');
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (appId: string) => {
    const app = apps.find(a => a.id === appId);
    if (!app) return;

    // Optimistic update
    setApps(prevApps =>
      prevApps.map(a =>
        a.id === appId ? { ...a, isFavorite: !a.isFavorite } : a
      )
    );

    try {
      if (app.isFavorite) {
        await dashboardApi.removeFavorite(appId);
      } else {
        await dashboardApi.addFavorite(appId);
      }
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
      // Revert optimistic update
      setApps(prevApps =>
        prevApps.map(a =>
          a.id === appId ? { ...a, isFavorite: !a.isFavorite } : a
        )
      );
    }
  };

  const logAccess = async (appId: string) => {
    try {
      await dashboardApi.logAccess(appId);
    } catch (err) {
      console.error('Failed to log access:', err);
    }
  };

  return {
    apps,
    favorites: apps.filter(app => app.isFavorite),
    loading,
    error,
    toggleFavorite,
    logAccess,
    refresh: loadApps,
  };
}
